import { useEffect, useReducer, useRef } from "react";
import {
  Campus,
  DistrictRef,
  ResponseError,
  ServiceAreaRef,
  ServiceProviderRef,
  SessionStatus,
  StudentRef,
} from "@xng/xng-backend";
import { useXNGSelector } from "../../../../../context/store";
import {
  selectLoggedInClientAssignment,
  selectUser,
} from "../../../../../context/slices/userProfileSlice";
import { API_SERVICEPROVIDERS_BACKEND, API_STATESNAPSHOTSNPM, API_USERS_BACKEND } from "../../../../../api/api";
import useEffectSkipMount from "../../../../../hooks/use_effect_after_mount";
import useIsReadOnlyCalendar from "../../../hooks/use_is_read_only_calendar";
import {
  CalendarFilterActionType as ActionType,
  CalendarFilterAction,
  CalendarFilterDispatchers,
  CalendarFilterReducer,
  CalendarFilterState,
} from "./types";

const initialState: CalendarFilterState = {
  selectedServiceAreas: [],
  selectedStudents: [],
  defaultAccountChecked: true,
  selectedAssistantRefs: [],
  fullSelectableAssistantList: [],
  fullSelectableServiceAreaList: [],
  fullSelectableStudentList: [],
  selectedServiceProviderIDs: [],
  selectedServiceProviderRefs: [],
  filtersDependencyArraySpreadable: [],
  sessionName: "",
  selectedDistrict: null,
  selectedCampus: null,
  selectedSessionStatus: null,
};

export function useCalendarFilterOptionsReducer(): CalendarFilterReducer {
  const [state, dispatch] = useReducer(filterOptionsReducer, initialState);

  const dispatchers: CalendarFilterDispatchers = {
    setSelectedServiceAreas: (serviceAreas: ServiceAreaRef[]) => {
      if (!serviceAreas) return;

      // Remove duplicates based on id
      const uniqueServiceAreas = serviceAreas.filter((area, index, self) =>
        index === self.findIndex(a => a.id === area.id)
      );

      dispatch({
        type: ActionType.SET_SELECTED_SERVICE_AREAS,
        payload: uniqueServiceAreas,
      });
    },
    setSelectedStudents: (students: StudentRef[]) => {
      if (!students) return;

      // Remove duplicates based on id
      const uniqueStudents = students.filter((student, index, self) =>
        index === self.findIndex(s => s.id === student.id)
      );

      dispatch({ type: ActionType.SET_SELECTED_STUDENTS, payload: uniqueStudents });
    },
    setSelectedAssistants: (assistantIDs: ServiceProviderRef[]) => {
      if (!assistantIDs) return;

      // Remove duplicates based on id
      const uniqueAssistants = assistantIDs.filter((assistant, index, self) =>
        index === self.findIndex(a => a.id === assistant.id)
      );

      dispatch({ type: ActionType.SET_SELECTED_ASSISTANT_IDS, payload: uniqueAssistants });
    },
    setDefaultAccountChecked: (value: boolean) =>
      dispatch({ type: ActionType.SET_DEFAULT_ACCOUNT_CHECKED, payload: value }),
    reset: () => dispatch({ type: ActionType.RESET }),
    // Full List Dispatchers
    setFullSelectableAssistantList: (assistants: ServiceProviderRef[]) => {
      dispatch({ type: ActionType.SET_FULL_SELECTABLE_ASSISTANT_LIST, payload: assistants });
    },
    setFullServiceAreaList: (serviceAreas: ServiceAreaRef[]) => {
      dispatch({ type: ActionType.SET_FULL_SERVICE_AREA_LIST, payload: serviceAreas });
    },
    setFullStudentList: (students: StudentRef[]) => {
      dispatch({ type: ActionType.SET_FULL_STUDENT_LIST, payload: students });
    },
    setSessionName: (v: string) => {
      dispatch({ type: ActionType.SET_SESSION_NAME, payload: v });
    },
    setSelectedDistrict: (district: DistrictRef | null) => {
      dispatch({ type: ActionType.SET_SELECTED_DISTRICT, payload: district });
    },
    setSelectedCampus: (campus: Campus | null) => {
      dispatch({ type: ActionType.SET_SELECTED_CAMPUS, payload: campus });
    },
    setSelectedSessionStatus: (status: SessionStatus | null) => {
      dispatch({ type: ActionType.SET_SELECTED_SESSION_STATUS, payload: status });
    },
  };

  const stateWithConstants: CalendarFilterState = {
    ...state,
    selectedServiceProviderIDs: useSelectedServiceProviderIDs(state),
    selectedServiceProviderRefs: useSelectedServiceProviderRefs(state),
    filtersDependencyArraySpreadable: [
      state.defaultAccountChecked,
      state.selectedAssistantRefs,
      state.selectedServiceAreas,
      state.selectedStudents,
      state.sessionName,
    ],
  };

  const reducer: CalendarFilterReducer = {
    ...stateWithConstants,
    ...dispatchers,
  };

  useFilterLifecycleEffects(reducer);

  return reducer;
}

function filterOptionsReducer(
  state: CalendarFilterState = initialState,
  action: CalendarFilterAction,
): CalendarFilterState {
  switch (action.type) {
    case ActionType.SET_SELECTED_SERVICE_AREAS:
      return { ...state, selectedServiceAreas: action.payload };
    case ActionType.SET_SELECTED_STUDENTS:
      return { ...state, selectedStudents: action.payload };
    case ActionType.SET_SELECTED_ASSISTANT_IDS:
      return { ...state, selectedAssistantRefs: action.payload };
    case ActionType.SET_FULL_SELECTABLE_ASSISTANT_LIST:
      return { ...state, fullSelectableAssistantList: action.payload };
    case ActionType.SET_DEFAULT_ACCOUNT_CHECKED:
      return { ...state, defaultAccountChecked: action.payload };
    case ActionType.SET_FULL_SERVICE_AREA_LIST:
      return { ...state, fullSelectableServiceAreaList: action.payload };
    case ActionType.SET_FULL_STUDENT_LIST:
      return { ...state, fullSelectableStudentList: action.payload };
    case ActionType.SET_SESSION_NAME:
      return { ...state, sessionName: action.payload };
    case ActionType.SET_SELECTED_DISTRICT:
      return { ...state, selectedDistrict: action.payload };
    case ActionType.SET_SELECTED_CAMPUS:
      return { ...state, selectedCampus: action.payload };
    case ActionType.SET_SELECTED_SESSION_STATUS:
      return { ...state, selectedSessionStatus: action.payload };
    case ActionType.RESET:
      return {
        ...state,
        defaultAccountChecked: true,
        selectedAssistantRefs: [],
        selectedServiceAreas: [],
        selectedStudents: [],
        selectedDistrict: null,
        selectedCampus: null,
        selectedSessionStatus: null,
      };
    default:
      throw new Error(
        "Fallthrough in Calendar switch statement! Has a new filter action been introduced?",
      );
  }
}

/**
 * At any point that data needs to be updated after its blank initialization, please place that here.
 */
function useFilterLifecycleEffects(reducer: CalendarFilterReducer) {
  const clientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const user = useXNGSelector(selectUser);
  const isReadOnly = useIsReadOnlyCalendar();
  const filtersLoaded = useRef(false);
  const readOnlyCaseloadCache = useRef(new Map<string, StudentRef[]>());

  // Persistence Logic: Write - Update showOnCalendar on individual resources
  useEffectSkipMount(() => {
    // The read-only admin calendar must never write filter selections back to the
    // backend — its selections are transient and district-wide, not the user's own
    // persisted showOnCalendar preferences.
    if (isReadOnly) return;

    // Skip writes until the initial read has populated state
    if (!filtersLoaded.current) return;

    const updateCalendarFilters = async () => {
      const serviceProviderId = clientAssignment?.serviceProviderProfile?.id;
      const clientId = clientAssignment?.client?.id;

      if (!serviceProviderId || !clientId) return;

      try {
        // 1. Update ServiceProvider showOnCalendar (for self)
        await API_SERVICEPROVIDERS_BACKEND.putCalendarVisibility({
          serviceProviderId,
          putCalendarVisibilityBody: {
            showOnCalendar: reducer.defaultAccountChecked,
          },
        });

        // 2. Update Caseloads showOnCalendar (for assistants)
        const allCaseloads = await API_SERVICEPROVIDERS_BACKEND.getCaseload({
          serviceProviderId,
          caseloadType: 1, // ServiceProvider caseload type
        });

        // Update each caseload based on whether it's selected
        for (const caseload of allCaseloads) {
          const isSelected = reducer.selectedAssistantRefs.some(
            (sp) => sp.id === caseload.id
          );

          if (caseload.showOnCalendar !== isSelected) {
            await API_SERVICEPROVIDERS_BACKEND.patchCaseload({
              serviceProviderId,
              caseloadType: 1,
              memberId: caseload.id!,
              patchCaseloadBody: {
                showOnCalendar: isSelected,
              },
            });
          }
        }

        // 3. Update StudentCaseloads showOnCalendar (for students)
        const allStudentCaseloads = await API_SERVICEPROVIDERS_BACKEND.getStudentCaseload({
          serviceProviderId,
        });

        // Update each student caseload based on whether it's selected
        for (const studentCaseload of allStudentCaseloads) {
          const isSelected = reducer.selectedStudents.some(
            (s) => s.id === studentCaseload.student?.id
          );

          if (studentCaseload.showOnCalendar !== isSelected) {
            await API_SERVICEPROVIDERS_BACKEND.putStudentCaseload({
              serviceProviderId,
              studentId: studentCaseload.student?.id!,
              putStudentCaseloadBody: {
                showOnCalendar: isSelected,
              },
            });
          }
        }

        // 4. Update ProviderTypes showOnCalendar (for service areas)
        const allProviderTypes = await API_USERS_BACKEND.getProviderTypes({
          id: user!.id!,
        });

        // Update each provider type based on whether it's selected
        for (const providerType of allProviderTypes) {
          const isSelected = reducer.selectedServiceAreas.some(
            (sa) => sa.id === providerType.id
          );

          if (providerType.showOnCalendar !== isSelected) {
            await API_USERS_BACKEND.putProviderType({
              id: user!.id!,
              providerType: providerType.id!,
              putProviderTypeBody: {
                showOnCalendar: isSelected,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error updating calendar filters:", error);
      }
    };

    updateCalendarFilters();
  }, [...reducer.filtersDependencyArraySpreadable]);

  // Persistence Logic: Read - Load from individual resources
  useEffect(() => {
    // Read-only admin mode: selections start clean and the service area options
    // span the state (all service areas). Providers are searched on demand by the
    // provider filter UI, not preloaded. Persisted showOnCalendar state is bypassed.
    if (isReadOnly) {
      reducer.reset();
      loadReadOnlyFilterOptions();
      return;
    }

    const loadCalendarFilters = async () => {
      const serviceProviderId = clientAssignment?.serviceProviderProfile?.id;
      const clientId = clientAssignment?.client?.id;

      if (!serviceProviderId || !clientId || !user?.id) return;

      try {
        // Set full selectable assistant list from supervised service providers
        reducer.setFullSelectableAssistantList(clientAssignment?.supervisedServiceProviders ?? []);

        // 1. Load ServiceProvider showOnCalendar (for self)
        // Fetch the full service provider profile to get showOnCalendar
        const serviceProviderProfile = await API_SERVICEPROVIDERS_BACKEND.getServiceProvider({
          serviceProviderId,
        });
        reducer.setDefaultAccountChecked(
          serviceProviderProfile.showOnCalendar ?? true
        );

        // 2. Load Caseloads showOnCalendar (for assistants)
        const allCaseloads = await API_SERVICEPROVIDERS_BACKEND.getCaseload({
          serviceProviderId,
          caseloadType: 1, // ServiceProvider caseload type
        });

        // Filter caseloads that have showOnCalendar = true
        const selectedCaseloads = allCaseloads.filter((c) => c.showOnCalendar === true);
        const selectedAssistants = clientAssignment?.supervisedServiceProviders?.filter((sp) =>
          selectedCaseloads.some((c) => c.id === sp.id)
        ) ?? [];
        reducer.setSelectedAssistants(selectedAssistants);

        // 3. Load StudentCaseloads showOnCalendar (for students)
        const allStudentCaseloads = await API_SERVICEPROVIDERS_BACKEND.getStudentCaseload({
          serviceProviderId,
        });

        // Filter student caseloads that have showOnCalendar = true
        const selectedStudentCaseloads = allStudentCaseloads.filter((sc) => sc.showOnCalendar === true);
        const selectedStudents = selectedStudentCaseloads
          .map((sc) => sc.student)
          .filter((s): s is StudentRef => s != null);
        reducer.setSelectedStudents(selectedStudents);

        // 4. Load ProviderTypes showOnCalendar (for service areas)
        const allProviderTypes = await API_USERS_BACKEND.getProviderTypes({
          id: user.id,
        });

        // Filter provider types that have showOnCalendar = true
        const selectedProviderTypes = allProviderTypes.filter((pt) => pt.showOnCalendar === true);
        const selectedServiceAreas = selectedProviderTypes
          .map((pt) => ({
            id: pt.id,
            name: pt.name,
          } as ServiceAreaRef))
          .filter((sa) => sa.id != null);
        reducer.setSelectedServiceAreas(selectedServiceAreas);
      } catch (error) {
        console.error("Error loading calendar filters:", error);
        // Fallback to empty selections on error
        reducer.setSelectedAssistants([]);
        reducer.setSelectedStudents([]);
        reducer.setSelectedServiceAreas([]);
      } finally {
        filtersLoaded.current = true;
      }
    };

    loadCalendarFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientAssignment?.supervisedServiceProviders, clientAssignment?.serviceProviderProfile, clientAssignment?.client?.id, user?.id, isReadOnly]);

  async function loadReadOnlyFilterOptions() {
    const clientId = clientAssignment?.client?.id;
    if (!clientId) return;

    try {
      const response = await API_STATESNAPSHOTSNPM.getServiceAreasByDate({
        date: new Date(),
        clientId,
        filterForMedicaidInfoOnly: false,
      });
      reducer.setFullServiceAreaList(response.serviceAreas ?? []);
    } catch (error) {
      // The endpoint 404s when the state/client has no service areas configured.
      if (error instanceof ResponseError && error.response.status === 404) {
        reducer.setFullServiceAreaList([]);
        return;
      }
      console.error("Error loading read-only calendar filter options:", error);
    }
  }

  // Read-only mode: student options are the selected provider's caseload,
  // fetched per provider and cached for the life of the page.
  useEffect(() => {
    if (!isReadOnly) return;

    const providerIds = reducer.selectedAssistantRefs
      .map((sp) => sp.id)
      .filter((id): id is string => Boolean(id));

    let cancelled = false;

    const loadStudentOptions = async () => {
      try {
        const caseloads = await Promise.all(
          providerIds.map(async (id) => {
            const cached = readOnlyCaseloadCache.current.get(id);
            if (cached) return cached;
            const studentCaseloads = await API_SERVICEPROVIDERS_BACKEND.getStudentCaseload({ serviceProviderId: id });
            const students = (studentCaseloads ?? [])
              .map((sc) => sc.student)
              .filter((s): s is StudentRef => s != null);
            readOnlyCaseloadCache.current.set(id, students);
            return students;
          }),
        );
        if (cancelled) return;

        const seen = new Set<string>();
        const union = caseloads
          .flat()
          .filter((s) => {
            if (!s.id || seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
          })
          .sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? ""));
        reducer.setFullStudentList(union);
      } catch (error) {
        console.error("Error loading read-only student filter options:", error);
      }
    };

    loadStudentOptions();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, reducer.selectedAssistantRefs]);
}

function useSelectedServiceProviderIDs(state: CalendarFilterState): string[] {
  const assistantIDs = state.selectedAssistantRefs?.map((a) => a.id!);
  const userID = useXNGSelector(selectLoggedInClientAssignment)?.serviceProviderProfile?.id!;
  // Read-only admins review other providers' sessions — their own account is
  // never an implicit selection.
  const isReadOnly = useIsReadOnlyCalendar();

  const res = [...assistantIDs];
  if (state.defaultAccountChecked && !isReadOnly) {
    res.push(userID);
  }

  return res;
}

function useSelectedServiceProviderRefs(state: CalendarFilterState): ServiceProviderRef[] {
  const res = [...state.selectedAssistantRefs];
  const user = useXNGSelector(selectLoggedInClientAssignment)?.serviceProviderProfile;

  if (state.defaultAccountChecked) {
    res.push(user!);
  }

  return res;
}
